import styles from "./FileSetting.module.css";
import Teams from "../components/Teams";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectTeams } from "../redux/slices/teamsSlice";
import { selectUser } from "../redux/slices/userSlice";
import {
  fetchFiles,
  uploadFiles,
  selectFile,
  clearFileError,
  clearUploadResult,
  deleteFile,
  clearDeleteResult,
} from "../redux/slices/fileSlice";
import { useToaster } from "../components/Toaster";
import CheckModal from '../components/CheckModal';

const ALLOW_EXT = [".pdf", ".txt"];
const MAX_FILE_MB = 100;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

function getExt(name = "") {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

function formatBytes(bytes) {
  const b = Number(bytes);
  if (!Number.isFinite(b) || b <= 0) return "-";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let u = 0;
  let v = b;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u += 1;
  }
  return `${v.toFixed(u === 0 ? 0 : 1)} ${units[u]}`;
}

export default function FileSetting() {
  const teams = useSelector(selectTeams);
  const user = useSelector(selectUser);
  const file = useSelector(selectFile);

  const dispatch = useDispatch();
  const { push } = useToaster();
  const [checkModalOpen, setCheckModalOpen] = useState(false);
  const [fileNameDelete, setFileNameDelete] = useState('');
  const [fileTokenDelete, setFileTokenDelete] = useState('');

  const isDisabled = useMemo(() => {
    const selected = teams.teams?.find((item) => item.token === teams.team_selected);
    const role = selected?.account_role;
    return Boolean((teams.teams?.length ?? 0) > 0 && ![1, 2].includes(role));
  }, [teams.teams, teams.team_selected]);

  const hasMounted = useRef(false);

  // 上傳表單狀態
  const inputRef = useRef(null);
  const [pickedFiles, setPickedFiles] = useState([]); // File[]
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // ✅ 這裡用 file.loading（你的 slice 目前 fetchFiles/uploadFiles 共用 loading）
  const canUpload = useMemo(() => {
    return (
      !isDisabled &&
      Boolean((teams?.team_selected ?? "").trim()) &&
      Boolean((user?.data?.token ?? "").trim()) &&
      !file.loading
    );
  }, [isDisabled, teams?.team_selected, user?.data?.token, file.loading]);

  // 依 team / user 抓清單
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    if (teams?.no_teams == false && (teams?.team_selected ?? "") !== "" && (user?.data?.token ?? "") !== "") {
      dispatch(
        fetchFiles({
          team_token: teams.team_selected,
          account_token: user.data.token,
        })
      );
    }
  }, [teams?.no_teams, teams?.team_selected, user?.data?.token, dispatch]);

  // team 換了，清空本次選擇（避免把 A 團隊檔案上傳到 B）
  useEffect(() => {
    setPickedFiles([]);
    setProgress({ done: 0, total: 0 });
  }, [teams?.team_selected]);

  // 顯示 Redux error
  useEffect(() => {
    const e = file.error;
    const msg = (e && (e.msg || e.message)) || "";

    if (msg) {
      push({ title: "發生錯誤", message: msg, variant: "danger" });
      dispatch(clearFileError());
    }
  }, [file.error, push, dispatch]);

  useEffect(() => {
    const r = file.uploadResult;
    const ok = (Array.isArray(r) && r.length > 0) || (!!r && !Array.isArray(r));

    if (!ok) return;

    push({
      title: "上傳完成",
      message: `已上傳 ${progress.total || pickedFiles.length || 0} 個檔案`,
      variant: "success",
    });

    setProgress((p) => ({ ...p, done: p.total || pickedFiles.length }));

    setPickedFiles([]);
    dispatch(clearUploadResult());

    if ((teams?.team_selected ?? "") !== "" && (user?.data?.token ?? "") !== "") {
      dispatch(fetchFiles({ team_token: teams.team_selected, account_token: user.data.token }));
    }
  }, [
    file.uploadResult,
    push,
    dispatch,
    teams?.team_selected,
    user?.data?.token,
    pickedFiles.length,
    progress.total,
  ]);

  useEffect(() => {
    const r = file.deleteResult;
    const ok = r.hasOwnProperty("file_token");

    if (!ok) return;

    push({
      title: "刪除完成",
      message: `已成功刪除 「${fileNameDelete}」`,
      variant: "success",
    });

    dispatch(clearDeleteResult());
    setCheckModalOpen(false);

    if ((teams?.team_selected ?? "") !== "" && (user?.data?.token ?? "") !== "") {
      dispatch(fetchFiles({ team_token: teams.team_selected, account_token: user.data.token }));
    }
  }, [
    file.deleteResult,
    push,
    dispatch,
    teams?.team_selected,
    user?.data?.token,
    pickedFiles.length,
    progress.total,
  ]);

  function addFiles(listLike) {
    const incoming = Array.from(listLike || []);
    if (incoming.length === 0) return;

    const ok = [];
    const bad = [];

    for (const f of incoming) {
      const ext = getExt(f.name);
      const extOk = ALLOW_EXT.includes(ext);
      const sizeOk = f.size <= MAX_FILE_BYTES;

      if (!extOk) {
        bad.push(`${f.name}（不支援的副檔名）`);
        continue;
      }
      if (!sizeOk) {
        bad.push(`${f.name}（超過 ${MAX_FILE_MB}MB）`);
        continue;
      }
      ok.push(f);
    }

    if (bad.length) {
      push({
        title: "檔案格式不符",
        message: bad.slice(0, 5).join("、") + (bad.length > 5 ? "…" : ""),
        variant: "warning",
      });
    }

    if (ok.length) {
      // 合併但去重（用 name + size）
      setPickedFiles((prev) => {
        const map = new Map(prev.map((f) => [`${f.name}__${f.size}`, f]));
        for (const f of ok) map.set(`${f.name}__${f.size}`, f);
        return Array.from(map.values());
      });
    }
  }

  function onPick(e) {
    addFiles(e.target.files);
    // 讓同一檔案可再選一次
    e.target.value = "";
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    addFiles(e.dataTransfer.files);
  }

  function openPicker() {
    if (!canUpload) return;
    inputRef.current?.click?.();
  }

  function removePicked(idx) {
    setPickedFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  function clearPicked() {
    setPickedFiles([]);
  }

  function onUpload() {
    if (!canUpload) return;

    const team_token = teams.team_selected;
    const account_token = user.data.token;

    if (!pickedFiles.length) {
      push({ title: "請先選擇檔案", message: "至少要選 1 個檔案才能上傳", variant: "warning" });
      return;
    }

    setProgress({ done: 0, total: pickedFiles.length });

    // ✅ 檔案上傳改走 redux（epic 送 multipart/form-data）
    dispatch(
      uploadFiles({
        team_token,
        account_token,
        files: pickedFiles, // ⚠️ File 是非序列化值：若 Redux middleware 警告，需在 store 裡忽略 uploadFiles action
      })
    );
  }

  const filesList = file?.files ?? [];

  const handleDeleteClick = (it) => {
    setFileTokenDelete(it?.file_token);
    setFileNameDelete(`${it?.file_name}${it?.ext}`);
    setCheckModalOpen(true);
  };

  const handleDelete = (file_token) => {
    const team_token = teams.team_selected;
    const account_token = user.data.token;

    dispatch(deleteFile({
      team_token,
      account_token,
      file_token,
    }));
  };

  return (
    <>
      <section className={styles.teamsWrap}>
        <div className={`${styles.panel} mt-2`}>
          <div className={`px-2 px-md-5 py-2 py-md-5 ${styles.content} row g-4`}>
            <div className="col-12">
              <div className={`p-md-4 ${styles.block}`}>
                <h4 className={styles.sectionTitle}>
                  <i className="bi bi-diagram-3 me-2" />
                  團隊選擇
                </h4>
                <div className="mt-2">
                  <Teams />
                </div>
              </div>
            </div>

            <div className="col-12 mt-2 mt-md-4">
              <div className={`p-md-4 ${styles.block}`}>
                <h4 className={styles.sectionTitle}>
                  <i className="bi bi-files me-2" />
                  檔案管理
                </h4>

                {/* 權限提示 */}
                {isDisabled && (
                  <div className="alert alert-warning mt-3 mb-3" role="alert">
                    你目前的權限無法上傳或管理檔案。
                  </div>
                )}

                {/* 上傳表單 */}
                <div className={`${styles.uploadToolbar} mt-3`}>
                  <div className={styles.uploadHint}>
                    支援 <b>.pdf</b> / <b>.txt</b>，單檔最大 <b>{MAX_FILE_MB}MB</b>
                  </div>

                  <div className={`${styles.toolbarBtns} mt-2 mt-md-0`}>
                    <button
                      type="button"
                      className={styles.btnClear}
                      onClick={clearPicked}
                      disabled={pickedFiles.length === 0 || file.loading}
                    >
                      <i className="bi bi-eraser me-1" />
                      清空
                    </button>
                    <button
                      type="button"
                      className={`ms-2 ${styles.btnSave}`}
                      onClick={onUpload}
                      disabled={!canUpload || pickedFiles.length === 0}
                    >
                      <i className="bi bi-send-check me-1" />
                      開始上傳
                    </button>
                  </div>
                </div>

                <input
                  ref={inputRef}
                  className={styles.hiddenInput}
                  type="file"
                  accept={ALLOW_EXT.join(",")}
                  multiple
                  onChange={onPick}
                  disabled={!canUpload}
                />

                <div
                  className={`${styles.dropZone} ${dragActive ? styles.dropZoneActive : ""} mt-3`}
                  role="button"
                  tabIndex={0}
                  onClick={openPicker}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") openPicker();
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(false);
                  }}
                  onDrop={onDrop}
                  aria-disabled={!canUpload}
                >
                  <div className={styles.dropTitle}>
                    <i className="bi bi-cloud-arrow-up me-2" />
                    拖曳檔案到這裡，或點擊選擇檔案
                  </div>
                  <div className={styles.dropSub}>一次可選多檔；會自動過濾副檔名與大小</div>
                </div>

                {/* 已選擇檔案 */}
                {pickedFiles.length > 0 && (
                  <div className={`${styles.pickedWrap} mt-3`}>
                    <div className={styles.pickedTitle}>本次準備上傳（{pickedFiles.length}）</div>
                    <div className={styles.pickedList}>
                      {pickedFiles.map((f, idx) => (
                        <div key={`${f.name}-${f.size}-${idx}`} className={styles.pickedItem}>
                          <div className={styles.pickedName} title={f.name}>
                            {f.name}
                          </div>
                          <div className={styles.pickedMeta}>{formatBytes(f.size)}</div>
                          <button
                            type="button"
                            className={styles.pickedRemove}
                            onClick={() => removePicked(idx)}
                            disabled={file.loading}
                            aria-label={`移除 ${f.name}`}
                            title="移除"
                          >
                            <i className="bi bi-x-lg" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 已上傳檔案清單 (更新樣式) */}
                <div className="mt-4">
                  <div className={styles.listHeader}>
                    <div className={styles.listTitle}>已上傳檔案</div>
                    <div className={styles.listSub}>{file?.loading ? "載入中…" : `共 ${filesList.length} 個`}</div>
                  </div>

                  {filesList.length === 0 && !file?.loading && (
                    <div className="mt-2 d-flex justify-content-center align-items-center">
                      <span className={`${styles.labelDot} me-2`} aria-hidden="true" />
                      目前無上傳中的檔案！
                    </div>
                  )}

                  {filesList.length > 0 && (
                    <div className={styles.fileList}>
                      {filesList.map((it, idx) => {
                        const name = `${it?.file_name}${it?.ext}`;
                        const time = it?.insert_time || '-';
                        const creater = it?.name || '-';

                        return (
                          <div key={`${name}-${idx}`} className={styles.fileRow}>
                            <div className={styles.fileMain}>
                              <div className={styles.fileName} title={name}>
                                <i className="bi bi-file-earmark-text me-2" />
                                {name}
                              </div>
                              <div className={styles.fileMeta}>
                                <span className={styles.badge}>
                                  <img
                                    src={it?.image}
                                    alt="image"
                                    className="rounded-circle me-1"
                                    style={{ width: 16, height: 16 }}
                                  />
                                  {creater}
                                </span>
                                <span className={styles.badge}>
                                  <i className="bi bi-clock me-1" />
                                  {String(time).replace("T", " ").slice(0, 19)}
                                </span>
                                <span className={styles.badge}>
                                  <i className="bi bi-robot me-1" />
                                  共 {it?.file_use_count} 個代理人使用
                                </span>
                              </div>
                            </div>
                            {/* 刪除按鈕 */}
                            <div>
                              <button
                                type="button"
                                className={styles.fileDeleteBtn}
                                onClick={() => handleDeleteClick(it)}
                                disabled={isDisabled}
                                aria-label={`刪除 ${name}`}
                                title="刪除"
                              >
                                <i className="bi bi-trash me-1" />
                                刪除
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <CheckModal
        modalOpen={checkModalOpen}
        title={'請確認'}
        msg={`您是否要刪除「${fileNameDelete}」？`}
        afterYes={() => handleDelete(fileTokenDelete)}
        afterNo={() => setCheckModalOpen(false)}
      />
    </>
  );
}